import { useCallback, useState } from "react";

import type { PendingApproval } from "../../shared/types";
import { RECAPTCHA_CLIENT_ARTIFACT_TOKEN } from "../hooks/recaptchaConstants";
import { useRecaptcha } from "../hooks/useRecaptcha";
import { type ApprovalRowValue, ToolApprovalCard } from "./ToolApprovalCard";

const SCF_TITLE = "Submit SeeClickFix Issue";
const SCF_RECAPTCHA_DISCLAIMER = "This site is protected by reCAPTCHA.";
const RECAPTCHA_VERIFICATION_FAILED = "reCAPTCHA verification failed.";

const ALLOWED_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

function tryParseAllowedUrl(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!URL.canParse(trimmed)) return null;
  const url = new URL(trimmed);
  return ALLOWED_SCHEMES.has(url.protocol) ? trimmed : null;
}

function formatScfRowValue(value: unknown, label: string): ApprovalRowValue {
  const url = tryParseAllowedUrl(value);
  if (url !== null) {
    return { kind: "link", href: url, text: label };
  }
  return { kind: "text", value };
}

interface ScfSubmitArgs {
  requestTypeId?: string | number;
  requestTypeTitle?: string;
  address?: unknown;
  answers?: Record<string, unknown>;
  questionLabels?: Record<string, string>;
  questionSelectValues?: Record<string, Record<string, string>>;
  [key: string]: unknown;
}

export interface ScfPendingApproval extends Omit<PendingApproval, "args"> {
  args: ScfSubmitArgs;
}

interface ScfToolApprovalCardProps {
  pendingApproval: ScfPendingApproval;
  onApprove?: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
  ) => void;
  onSkip?: () => void;
  isLoading: boolean;
  primaryColor: string;
  siteKey: string;
  action: string;
  error?: string;
  onLinkClick?: (url: string, linkText: string) => void;
}

const ISSUE_IMAGE_KEY_PATTERN = /^issue_image(\d*)$/;

function getAnswerLabel(
  key: string,
  questionLabels?: Record<string, string>,
): string {
  const imageMatch = ISSUE_IMAGE_KEY_PATTERN.exec(key);
  if (imageMatch !== null) {
    const imageNumber = Number(imageMatch[1] || 0) + 1;
    const baseLabel = questionLabels?.["issue_image"] ?? "Image";
    return `${baseLabel} ${imageNumber}`;
  }
  return questionLabels?.[key] ?? key;
}

function buildReadableArgs(
  args: ScfSubmitArgs,
): { label: string; value: ApprovalRowValue }[] {
  const result: { label: string; value: ApprovalRowValue }[] = [];

  const requestTypeDisplay = args.requestTypeTitle ?? args.requestTypeId;
  if (requestTypeDisplay != null) {
    result.push({
      label: "Request Type",
      value: formatScfRowValue(requestTypeDisplay, "Request Type"),
    });
  }

  if (args.address != null) {
    result.push({
      label: "Location",
      value: formatScfRowValue(args.address, "Location"),
    });
  }

  if (args.answers != null) {
    for (const [key, value] of Object.entries(args.answers)) {
      const label = getAnswerLabel(key, args.questionLabels);
      const selectMap = args.questionSelectValues?.[key];
      const displayValue = selectMap?.[String(value)] ?? value;
      result.push({ label, value: formatScfRowValue(displayValue, label) });
    }
  }

  return result;
}

export function ScfToolApprovalCard({
  pendingApproval,
  onApprove,
  onSkip,
  isLoading,
  primaryColor,
  siteKey,
  action,
  error,
  onLinkClick,
}: ScfToolApprovalCardProps) {
  const { isRecaptchaReady, recaptchaError, resolveToken } = useRecaptcha(
    siteKey,
    action,
  );
  const [isPending, setIsPending] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const handleApprove = useCallback(
    async (decision: "approved" | "denied") => {
      if (decision === "denied") {
        onApprove?.("denied");
        return;
      }

      setIsPending(true);
      setResolveError(null);

      try {
        const token = await resolveToken();
        onApprove?.("approved", { [RECAPTCHA_CLIENT_ARTIFACT_TOKEN]: token });
      } catch (err) {
        setResolveError(
          err instanceof Error ? err.message : RECAPTCHA_VERIFICATION_FAILED,
        );
      } finally {
        setIsPending(false);
      }
    },
    [onApprove, resolveToken],
  );

  const recaptchaErrorMessage = recaptchaError ?? resolveError;

  return (
    <ToolApprovalCard
      rows={buildReadableArgs(pendingApproval.args)}
      onApprove={handleApprove}
      onSkip={onSkip}
      isLoading={isLoading || isPending}
      primaryColor={primaryColor}
      isApproveDisabled={!isRecaptchaReady || !!recaptchaError || isPending}
      approveLabel="Submit"
      denyLabel="Make Changes"
      disclaimer={recaptchaErrorMessage ?? SCF_RECAPTCHA_DISCLAIMER}
      error={error}
      title={SCF_TITLE}
      onLinkClick={onLinkClick}
    />
  );
}
